import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { PlusIcon, ReceiptIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { NONE, Select } from "@/components/ui/select";
import { useFormat } from "@/lib/format";
import { useModule } from "@/lib/modules";
import { useTRPC } from "@/lib/trpc";
import { ExpenseRow, useExpenseActions } from "./expense-list";
import { useHiddenExpenses } from "./shared";

/**
 * A work order's side column (`WorkOrderSections`): who did it, and its invoices with their total
 * beside the parts cost above.
 */
export function WorkOrderSection({ workOrderId }: { workOrderId: string }) {
  const active = useModule("vehicleManagement");
  if (!active) return null;
  return (
    <>
      <PerformedByCard workOrderId={workOrderId} />
      <InvoicesCard workOrderId={workOrderId} />
    </>
  );
}

function PerformedByCard({ workOrderId }: { workOrderId: string }) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const { data } = useQuery(trpc.vehicleManagement.workOrder.queryOptions({ workOrderId }));
  const { data: providers = [] } = useQuery(trpc.vehicleManagement.serviceProviders.queryOptions());
  const set = useMutation(trpc.vehicleManagement.setPerformedBy.mutationOptions());
  if (!data) return null;
  const current = data.performedBy;
  // One that is no longer flagged stays choosable while it is set.
  const options =
    current && !providers.some((p) => p.id === current.id) ? [...providers, current] : providers;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("vehicleManagement.performedBy")}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <Select
          aria-label={t("vehicleManagement.performedBy")}
          value={current?.id ?? NONE}
          disabled={set.isPending}
          onValueChange={(v) => set.mutate({ workOrderId, supplierId: v === NONE ? null : v })}
          options={[
            { value: NONE, label: t("vehicleManagement.doneYourself") },
            ...options.map((p) => ({ value: p.id, label: p.name })),
          ]}
        />
        {providers.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            {t("vehicleManagement.noProviders")}{" "}
            <Link to="/settings/suppliers" className="underline underline-offset-2">
              {t("vehicleManagement.toSuppliers")}
            </Link>
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function InvoicesCard({ workOrderId }: { workOrderId: string }) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const format = useFormat();
  const actions = useExpenseActions();
  const hidden = useHiddenExpenses();
  const { data } = useQuery(trpc.vehicleManagement.workOrder.queryOptions({ workOrderId }));
  if (!data) return null;
  const rows = data.expenses.filter((e) => !hidden.has(e.id));
  const add = () =>
    actions.add({
      vehicleId: data.vehicleId,
      workOrderId,
      costType: data.invoiceDraft.costType,
      supplierId: data.performedBy?.id ?? null,
      ...(data.invoiceDraft.spentOn ? { spentOn: data.invoiceDraft.spentOn } : {}),
    });
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("vehicleManagement.invoices")}</CardTitle>
        <Button variant="ghost" size="sm" onClick={add}>
          <PlusIcon />
          {t("vehicleManagement.addInvoice")}
        </Button>
      </CardHeader>
      {rows.length ? (
        <>
          <CardContent className="pb-2">
            <div className="text-2xl font-semibold tracking-tight tabular">
              {format.money(data.totals.totalMinor, data.totals.currency)}
            </div>
          </CardContent>
          <div className="divide-y divide-border border-t border-border">
            {rows.map((expense) => (
              <ExpenseRow
                key={expense.id}
                expense={expense}
                actions={actions}
                showVehicle={false}
              />
            ))}
          </div>
        </>
      ) : (
        <CardContent>
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <ReceiptIcon className="size-4 shrink-0" />
            {t("vehicleManagement.invoicesEmpty")}
          </p>
        </CardContent>
      )}
      {actions.sheet}
    </Card>
  );
}
